import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import type { Employee } from '@khadi/shared';
import { api, ApiError } from '../lib/api';
import { useLocations } from '../lib/queries';

/**
 * Neue Person anlegen — von der Mitarbeiterliste und mitten aus einer
 * Übergabe heraus. Wer ein Gerät erfasst, hat den neuen Kollegen oft vor
 * sich stehen; ihn erst auf der Personenseite anlegen zu müssen, reißt
 * den Vorgang auseinander.
 */
export function EmployeeCreateDialog({
  defaultName,
  onClose,
  onCreated,
}: {
  /** Vorbelegung aus einer Suche, die nichts gefunden hat („Anna Meyer"). */
  defaultName?: string;
  onClose: () => void;
  onCreated?: (employee: Employee) => void;
}) {
  const queryClient = useQueryClient();
  const locations = useLocations();
  const [form, setForm] = useState(() => ({
    ...splitName(defaultName ?? ''),
    email: '',
    employee_no: '',
    location_id: 0,
  }));

  const create = useMutation({
    mutationFn: () =>
      api<Employee>('/employees', {
        method: 'POST',
        body: {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim() || null,
          employee_no: form.employee_no.trim() || null,
          location_id: form.location_id || null,
        },
      }),
    onSuccess: (employee) => {
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast.success(`${employee.name} angelegt`);
      onCreated?.(employee);
      onClose();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Anlegen fehlgeschlagen'),
  });

  // Als Portal an <body>: Der Dialog wird aus der Personenauswahl heraus
  // geöffnet, die selbst im Erfassungsformular steckt — verschachtelte
  // Formulare sind ungültig.
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-[var(--color-khadi-forest-900)]/45 backdrop-blur-[2px] sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Mitarbeiter anlegen"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="animate-sheet-up safe-bottom w-full max-w-md rounded-t-[var(--radius-xl)] border border-[var(--color-line)] bg-[var(--color-bg-elevated)] p-5 shadow-[var(--shadow-lg)] sm:rounded-[var(--radius-xl)]">
        <h2 className="font-[family-name:var(--font-display)] mb-5 text-2xl">Mitarbeiter anlegen</h2>

        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            // React reicht Ereignisse auch durch Portale an den Elternbaum
            // weiter — ohne das hier legt das Erfassungsformular das Gerät an.
            event.stopPropagation();
            create.mutate();
          }}
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="emp-first">
                Vorname
              </label>
              <input
                id="emp-first"
                autoFocus
                className="field"
                value={form.first_name}
                onChange={(event) => setForm({ ...form, first_name: event.target.value })}
                required
              />
            </div>
            <div>
              <label className="label" htmlFor="emp-last">
                Nachname
              </label>
              <input
                id="emp-last"
                className="field"
                value={form.last_name}
                onChange={(event) => setForm({ ...form, last_name: event.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <label className="label" htmlFor="emp-email">
              E-Mail <span className="font-normal normal-case">(für Empfangsbestätigungen)</span>
            </label>
            <input
              id="emp-email"
              type="email"
              className="field"
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="emp-no">
                Personalnummer
              </label>
              <input
                id="emp-no"
                className="field tabular"
                value={form.employee_no}
                onChange={(event) => setForm({ ...form, employee_no: event.target.value })}
              />
            </div>
            <div>
              <label className="label" htmlFor="emp-location">
                Standort
              </label>
              <select
                id="emp-location"
                className="field"
                value={form.location_id}
                onChange={(event) => setForm({ ...form, location_id: Number(event.target.value) })}
              >
                <option value={0}>— keiner —</option>
                {locations.data?.rows.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn btn-ghost btn-lg flex-1">
              Abbrechen
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-lg flex-1"
              disabled={create.isPending || !form.first_name.trim() || !form.last_name.trim()}
            >
              <Plus className="h-4 w-4" aria-hidden />
              {create.isPending ? 'Wird angelegt …' : 'Anlegen'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

/**
 * „Anna Meyer" → Vorname „Anna", Nachname „Meyer".
 * Ein einzelnes Wort gilt als Vorname — der Nachname bleibt Pflichtfeld
 * und wird sichtbar leer, statt geraten zu werden.
 */
function splitName(input: string): { first_name: string; last_name: string } {
  const parts = input.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first_name: '', last_name: '' };
  return { first_name: parts[0]!, last_name: parts.slice(1).join(' ') };
}
